import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { mdiPencil } from '@mdi/js'
import { useTranslation } from 'react-i18next'
import { useUpdateCampaign, type Campaign, type CampaignInput } from '../../hooks/useCampaigns'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { EditableValue } from '../ui/editable-value'
import { AppIcon } from '../ui/icon'
import { Input } from '../ui/input'
import { AgentsPicker, ScopePicker, type CampaignAgent, type ScopeAsset } from './CampaignPickers'
import { itemLabel } from './CampaignEntryField'
import { formatDate } from './CampaignsPage'

/**
 * What stays editable until the close (spec 2026-10-20-12 §1): the name, the
 * due date, the data year, the agents and the perimeter. The questionnaire is
 * shown, and editable on a draft only.
 */
export function CampaignSettingsTab({ org, campaign }: { org: string; campaign: Campaign }) {
    const { t, i18n } = useTranslation('campaigns')
    const update = useUpdateCampaign(org, campaign.uid)
    const [error, setError] = useState<string | null>(null)
    const editable = campaign.status !== 'closed'
    const save = (body: CampaignInput) => update.mutateAsync(body).then(() => setError(null), (e: Error) => setError(e.message))

    return (
        <div className="grid gap-4 lg:grid-cols-2" data-testid="campaign-settings">
            {error && <p className="text-sm text-destructive lg:col-span-2" role="alert" data-testid="campaign-settings-error">{error}</p>}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">{t('settings.general')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                    <Field label={t('name')}>
                        <InlineText value={campaign.name} canEdit={editable} onSave={(v) => save({ name: v })} testId="campaign-name" type="text" />
                    </Field>
                    <Field label={t('due_date')}>
                        <InlineText value={campaign.due_on ?? ''} display={formatDate(campaign.due_on)} canEdit={editable}
                            onSave={(v) => save({ due_on: v })} testId="campaign-due-on" type="date" />
                    </Field>
                    <Field label={t('data_year')}>
                        <InlineText value={campaign.data_year ? String(campaign.data_year) : ''} canEdit={editable}
                            onSave={(v) => save({ data_year: Number(v) })} testId="campaign-data-year" type="number" />
                    </Field>
                    <Field label={t('level_label')}>{t(`level.${campaign.level}`)}</Field>
                </CardContent>
            </Card>
            <SetCard
                title={t('agents')}
                editable={editable}
                testId="campaign-agents-card"
                summary={
                    campaign.agent_list.length === 0 ? (
                        <p className="text-muted-foreground">{t('agents_empty')}</p>
                    ) : (
                        <ul className="space-y-1">
                            {campaign.agent_list.map((a) => (
                                <li key={a.uid}>{a.name || a.email}</li>
                            ))}
                        </ul>
                    )
                }
                editor={(close) => (
                    <AgentsEditor org={org} initial={campaign.agent_list.map((a) => ({ uid: a.uid, name: a.name || a.email || a.uid }))}
                        onSave={(agents) => save({ agent_uids: agents.map((a) => a.uid) }).then(close)} onCancel={close} />
                )}
            />
            <SetCard
                title={t('scope')}
                editable={editable}
                testId="campaign-scope-card"
                summary={
                    <div className="flex flex-wrap gap-2">
                        {campaign.scope.map((a) => (
                            <Badge key={a.uid} variant="secondary">{a.name}</Badge>
                        ))}
                    </div>
                }
                editor={(close) => (
                    <ScopeEditor org={org} initial={campaign.scope.map((a) => ({ uid: a.uid, name: a.name }))}
                        onSave={(uids) => save({ scope_asset_uids: uids }).then(close)} onCancel={close} />
                )}
            />
            <Card data-testid="campaign-questionnaire">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-base">{t('questionnaire')}</CardTitle>
                    {campaign.status === 'draft' ? (
                        <Button variant="outline" size="icon" asChild aria-label={t('edit_questionnaire')} title={t('edit_questionnaire')}>
                            <Link to="/$org/campaigns/$campaignUid/edit" params={{ org, campaignUid: campaign.uid }} data-testid="campaign-questionnaire-edit">
                                <AppIcon path={mdiPencil} size="sm" />
                            </Link>
                        </Button>
                    ) : (
                        <span className="text-xs text-muted-foreground">{t('questionnaire_frozen')}</span>
                    )}
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                    {campaign.groupings.map((g) => (
                        <div key={g.uid}>
                            <p className="font-medium">{g.name}</p>
                            <ul className="list-disc pl-5 text-muted-foreground">
                                {g.items.map((it) => (
                                    <li key={it.uid}>
                                        {itemLabel(it, i18n.language.slice(0, 2))}
                                        {it.required && ' *'}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </CardContent>
            </Card>
        </div>
    )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="grid grid-cols-[8rem_1fr] items-center gap-2">
            <span className="text-muted-foreground">{label}</span>
            <div>{children}</div>
        </div>
    )
}

function InlineText({ value, display, canEdit, onSave, testId, type }: {
    value: string
    display?: string
    canEdit: boolean
    onSave: (v: string) => Promise<unknown>
    testId: string
    type: 'text' | 'date' | 'number'
}) {
    const { t } = useTranslation('campaigns')
    const [editing, setEditing] = useState(false)
    const [draft, setDraft] = useState(value)
    useEffect(() => setDraft(value), [value])
    if (!editing) {
        return (
            <EditableValue canEdit={canEdit} onEdit={() => setEditing(true)} title={t('click_to_edit')} placeholder={t('not_set')} data-testid={testId}>
                {display ?? value}
            </EditableValue>
        )
    }
    const commit = () => {
        setEditing(false)
        if (draft !== value) void onSave(draft)
    }
    return (
        <Input
            autoFocus
            type={type}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === 'Enter') commit()
                if (e.key === 'Escape') {
                    setDraft(value)
                    setEditing(false)
                }
            }}
            data-testid={`${testId}-input`}
        />
    )
}

/** A card editing a set, with its card-level pencil (patterns §31). */
function SetCard({ title, editable, summary, editor, testId }: {
    title: string
    editable: boolean
    summary: React.ReactNode
    editor: (close: () => void) => React.ReactNode
    testId: string
}) {
    const { t } = useTranslation('campaigns')
    const [editing, setEditing] = useState(false)
    return (
        <Card data-testid={testId}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-base">{title}</CardTitle>
                {editable && !editing && (
                    <Button variant="outline" size="icon" onClick={() => setEditing(true)} aria-label={t('edit')} title={t('edit')} data-testid={`${testId}-edit`}>
                        <AppIcon path={mdiPencil} size="sm" />
                    </Button>
                )}
            </CardHeader>
            <CardContent className="text-sm">{editing ? editor(() => setEditing(false)) : summary}</CardContent>
        </Card>
    )
}

function AgentsEditor({ org, initial, onSave, onCancel }: { org: string; initial: CampaignAgent[]; onSave: (agents: CampaignAgent[]) => void; onCancel: () => void }) {
    const { t } = useTranslation('campaigns')
    const [value, setValue] = useState(initial)
    return (
        <div className="space-y-2">
            <AgentsPicker org={org} value={value} onChange={setValue} />
            <EditorButtons onSave={() => onSave(value)} onCancel={onCancel} saveLabel={t('save')} cancelLabel={t('cancel')} testId="campaign-agents" />
        </div>
    )
}

function ScopeEditor({ org, initial, onSave, onCancel }: { org: string; initial: ScopeAsset[]; onSave: (uids: string[]) => void; onCancel: () => void }) {
    const { t } = useTranslation('campaigns')
    const [value, setValue] = useState(initial)
    return (
        <div className="space-y-2">
            <ScopePicker org={org} value={value} onChange={setValue} />
            <EditorButtons onSave={() => onSave(value.map((a) => a.uid))} onCancel={onCancel} saveLabel={t('save')} cancelLabel={t('cancel')}
                disabled={value.length === 0} testId="campaign-scope" />
        </div>
    )
}

function EditorButtons({ onSave, onCancel, saveLabel, cancelLabel, disabled, testId }: {
    onSave: () => void
    onCancel: () => void
    saveLabel: string
    cancelLabel: string
    disabled?: boolean
    testId: string
}) {
    return (
        <div className="flex gap-2">
            <Button size="sm" onClick={onSave} disabled={disabled} data-testid={`${testId}-save`}>{saveLabel}</Button>
            <Button size="sm" variant="outline" onClick={onCancel} data-testid={`${testId}-cancel`}>{cancelLabel}</Button>
        </div>
    )
}
